import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, ChevronDown } from 'lucide-react';

const ButtonSpinner = () => (
  <svg className="w-3.5 h-3.5 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4z" />
  </svg>
);

function pad(n) {
  return String(n).padStart(2, '0');
}

function toYMD(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

// Story 25 — the three period choices the story specifies: last complete calendar month
// (default), this month to date, and an ad-hoc custom range (covers a 26-25 cutoff, a weekly
// run, a departing employee's final settlement). No persistent cutoff-day setting — the range
// is chosen fresh at export time.
export function lastCompleteMonthRange(today = new Date()) {
  const from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const to = new Date(today.getFullYear(), today.getMonth(), 0);
  return { from: toYMD(from), to: toYMD(to) };
}

export function thisMonthToDateRange(today = new Date()) {
  const from = new Date(today.getFullYear(), today.getMonth(), 1);
  return { from: toYMD(from), to: toYMD(today) };
}

// Story 25 — mirrors ExportCsvButton.jsx's split-button dropdown (no isGated/disabled wiring:
// export must stay enabled in every subscription state for DPDP portability), with a custom
// date-range sub-panel for the ad-hoc case.
function MusterExportButton({ onExport, exporting }) {
  const { t } = useTranslation('reports');
  const [open, setOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handleMouseDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setCustomOpen(false);
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [open]);

  const closeAndExport = (from, to) => {
    setOpen(false);
    setCustomOpen(false);
    onExport(from, to);
  };

  const handleLastCompleteMonth = () => {
    const { from, to } = lastCompleteMonthRange();
    closeAndExport(from, to);
  };
  const handleThisMonth = () => {
    const { from, to } = thisMonthToDateRange();
    closeAndExport(from, to);
  };

  const handleCustomSubmit = () => {
    if (!customFrom || !customTo) return;
    closeAndExport(customFrom, customTo);
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={exporting}
        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-orange-700 border border-orange-300 bg-white hover:bg-orange-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {exporting ? <ButtonSpinner /> : <Download size={15} />}
        {t('musterExport.button')}
        <ChevronDown size={14} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1.5 w-72 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden z-50">
          <button
            type="button"
            onClick={handleLastCompleteMonth}
            className="w-full flex flex-col items-start px-4 py-2.5 text-left hover:bg-gray-50 transition-colors"
          >
            <span className="text-sm font-medium text-gray-800">{t('musterExport.lastCompleteMonth')}</span>
            <span className="text-xs text-gray-400">{t('musterExport.lastCompleteMonthHint')}</span>
          </button>
          <button
            type="button"
            onClick={handleThisMonth}
            className="w-full flex flex-col items-start px-4 py-2.5 text-left hover:bg-gray-50 transition-colors border-t border-gray-100"
          >
            <span className="text-sm font-medium text-gray-800">{t('musterExport.thisMonth')}</span>
            <span className="text-xs text-gray-400">{t('musterExport.thisMonthHint')}</span>
          </button>
          <button
            type="button"
            onClick={() => setCustomOpen((c) => !c)}
            className="w-full flex flex-col items-start px-4 py-2.5 text-left hover:bg-gray-50 transition-colors border-t border-gray-100"
          >
            <span className="text-sm font-medium text-gray-800">{t('musterExport.customRange')}</span>
            <span className="text-xs text-gray-400">{t('musterExport.customRangeHint')}</span>
          </button>
          {customOpen && (
            <div className="px-4 py-3 border-t border-gray-100 bg-gray-50 space-y-2">
              <label className="block text-xs text-gray-600">
                {t('musterExport.fromLabel')}
                <input
                  type="date"
                  value={customFrom}
                  onChange={(e) => setCustomFrom(e.target.value)}
                  className="mt-1 w-full px-2 py-1.5 rounded-md border border-gray-300 bg-white text-sm text-gray-900 outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-500"
                />
              </label>
              <label className="block text-xs text-gray-600">
                {t('musterExport.toLabel')}
                <input
                  type="date"
                  value={customTo}
                  onChange={(e) => setCustomTo(e.target.value)}
                  className="mt-1 w-full px-2 py-1.5 rounded-md border border-gray-300 bg-white text-sm text-gray-900 outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-500"
                />
              </label>
              <button
                type="button"
                onClick={handleCustomSubmit}
                disabled={!customFrom || !customTo}
                className="w-full mt-1 px-3 py-1.5 rounded-md bg-orange-500 text-white text-sm font-medium hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {t('musterExport.downloadButton')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default MusterExportButton;
